import { pipe } from 'ramda'
import { graphql, useStaticQuery, withPrefix } from 'gatsby'
import {
  Maybe,
  ProductItemComponentType,
  ProductItemType,
  ProductVariantType,
} from '~/@types/models'
import { useState, useCallback, useEffect, useMemo } from 'react'
import { getProductImage, getProductVariant } from '~/containers/collection-products/utils'
import { EFilterName } from '~/components/filter'
import { FilterType, useFilterContext } from '~/context/filters'
type Props<T> = {
  collectionHandle: string
  filter?: FilterType
  gridVariant: string
  initialData: readonly T[]
  collectionProducts: readonly T[]
  template?: 'lastChance' | 'inStock'
}

export type TSort = 'price_desc' | 'price_asc' | 'recommended' | null
export type TSortAction = React.Dispatch<React.SetStateAction<TSort>>

interface UseCollectionData<T> {
  isFetching: boolean
  isInitialized: boolean
  currentFilters: FilterType | undefined
  resetFilters: () => void
  setFilters: (filters: FilterType) => void
  setSort: TSortAction
  sort: TSort
  currentData: readonly T[]
}

const api = (collectionHandle: string) => withPrefix(`/collection-data/${collectionHandle}.json`)

const filterByMetafield = (product: ProductItemType, filterOptions: any[], metafield: string) => {
  let found
  filterOptions.forEach((option: any) => {
    const _metafield = metafield as keyof ProductItemType['metafields']
    const metafieldValue = product.metafields[_metafield]

    if (metafieldValue) {
      if (
        ((typeof metafieldValue === 'string' || Array.isArray(metafieldValue)) &&
          metafieldValue.includes(option.value)) ||
        metafieldValue === option.value
      ) {
        found = product
      }
    }
  })
  return found
}

const filterFields = ['productType', 'inStock', 'collectionType']

const filterByProductField = (product: ProductItemType, filterOptions: any[], field: string) => {
  let found
  filterOptions.forEach((option: any) => {
    const _field = product[field as keyof ProductItemType]
    if (_field) {
      if (Array.isArray(_field)) {
        if (_field.includes(option.value)) {
          found = product
        }
      } else if (_field === option.value) {
        found = product
      }
    }
  })
  return found
}

export interface TProductWithGrid extends Omit<ProductItemType, 'price'> {
  gridLayout: string
  price?: {
    amount: string
  }
  productVariant: Maybe<ProductVariantType>
}

const useCollectionData = <T extends TProductWithGrid>({
  collectionHandle,
  gridVariant,
  initialData,
  collectionProducts,
  template,
}: Props<T>): UseCollectionData<T> => {
  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [data, setData] = useState<readonly T[]>(initialData)
  const [sort, setSort] = useState<TSort>(null)
  const [isInitialized, setIsInitialized] = useState<boolean>(false)
  const { currentFilters, setCurrentFilters } = useFilterContext()

  const fetchData = useCallback(async (collectionHandle: string) => {
    const res = await fetch(api(collectionHandle))
    const json = await res.json()
    let productsData: T[] = json?.data ? json.data : []

    productsData = productsData?.map(item => {
      return {
        ...item,
        mainImage: getProductImage(item, collectionHandle),
        productVariant: getProductVariant(item as ProductItemComponentType),
        gridLayout: gridVariant.replace('layout.', '') as any,
      }
    })

    // if (template === 'lastChance') {
    //   productsData = buildLastChanceProducts(productsData)
    // }

    // if (template === 'inStock') {
    //   productsData = buildInStockProducts(productsData)
    // }

    return productsData
  }, [])

  const filterData = useCallback(
    (productsData: T[]) => {
      const selectedFilters = () => {
        const filters: string[] = []
        if (currentFilters?.selectedFilters) {
          Object.keys(currentFilters.selectedFilters).forEach((option: any) => {
            filters.push(option)
          })
        }
        return filters
      }

      const isolateCategoryName = (categoryName: string) => {
        const foundCategory = currentFilters?.selectedFilters
          ? currentFilters?.selectedFilters[categoryName]
          : []

        if (foundCategory) {
          return foundCategory
        }
      }

      let filteredData = productsData

      if (currentFilters && currentFilters.selectedFilters) {
        const selectedOptions = selectedFilters()

        const dataFilter = (products: any[], option: string) =>
          products.filter((product: ProductItemType) => {
            if (currentFilters.selectedFilters) {
              const valuesArray = isolateCategoryName(option) || []

              if (filterFields.includes(option)) {
                return pipe(filterByProductField)(product, valuesArray, option)
              }

              return pipe(filterByMetafield)(product, valuesArray, option)
            }
            return product
          })

        selectedOptions.forEach(option => {
          filteredData = dataFilter(filteredData, option)
        })
      }

      return filteredData
    },
    [currentFilters]
  )
  const getPrice = (product: T) => {
    const price = (product.cheapestVariant?.price?.amount || product.price?.amount) as string
    return parseFloat(price)
  }

  const sortData = useCallback(
    (products: T[]): T[] => {
      switch (sort) {
        case 'price_asc':
          return products.sort((a, b) => {
            return getPrice(a) - getPrice(b)
          })
        case 'price_desc':
          return products.sort((a, b) => getPrice(b) - getPrice(a))
        case 'recommended':
          return products
        default:
          return products
      }
    },
    [sort]
  )

  const resetFilters = async () => {
    setIsFetching(true)
    setCurrentFilters({})
    localStorage.removeItem(EFilterName.filter)
  }

  useEffect(() => {
    if (isInitialized || isFetching) {
      return
    }
    setIsFetching(true)

    if (collectionProducts) {
      setData(collectionProducts)
      setIsInitialized(true)
      setIsFetching(false)
      return
    }

    try {
      fetchData(collectionHandle)
        .then(products => {
          setData(products)
        })
        .finally(() => {
          setIsInitialized(true)
          setIsFetching(false)
        })
    } catch {
      setIsInitialized(false)
      setIsFetching(false)
    }
  }, [isInitialized, isFetching, collectionHandle])

  const currentData = useMemo(() => {
    return sortData(filterData([...data]))
  }, [data, filterData, sortData])

  return {
    isFetching,
    isInitialized,
    currentFilters,
    resetFilters,
    setFilters: (filters: FilterType) => {
      setCurrentFilters(filters)
    },
    setSort,
    sort,
    currentData,
  }
}

export default useCollectionData
